/**
 * web/src/shell/tabbar.tsx — the bottom rail (mobile) / side menu (desktop):
 * the tab bar with its Explore slot and collapse toggle, moved verbatim out
 * of App.tsx (card WUHnArc6lNID).
 */
import type { Dispatch, SetStateAction } from 'react';
import { TabBar } from '../components/TabBar';
import { TABS } from './tabs';
import type { Dest, Tab } from './dest';

interface ShellTabBarProps {
  onChat: boolean;
  tab: Tab;
  extraView: 'help' | null;
  settingsOpen: boolean;
  showingAI: boolean;
  moreOpen: boolean;
  setMoreOpen: Dispatch<SetStateAction<boolean>>;
  railCollapsed: boolean;
  toggleRail: () => void;
  navigateTo: (dest: Dest) => void;
}

export function ShellTabBar({ onChat, tab, extraView, settingsOpen, showingAI, moreOpen, setMoreOpen, railCollapsed, toggleRail, navigateTo }: ShellTabBarProps) {
  return (
    <>
    {/* Nav — mobile: a bar fixed to the viewport bottom (thumb zone);
        desktop ≥641px: the same element becomes a pinned side menu down
        the left edge (card ESnACmbVhCYd). Both anchor to the layout
        viewport, so scrolling inside .pear-main or a panel never moves it. */}
    {onChat && (
      <TabBar
        tabs={TABS}
        active={tab}
        /* A view opened from Explore (Help, Vault, Settings, the AI screen)
           has no tab of its own, so the bar underlines the door it came
           through instead of the tab that happened to be active before.
           Card HXzRl6doT9DQ: Search is one of those views again — it left the
           rail, so `tab === 'search'` must light the Explore slot exactly as
           Settings does (the bug card gb7a2kWmEPT6 fixed for the other five). */
        trailingActive={Boolean(extraView || settingsOpen || showingAI || tab === 'search')}
        panelFor={key =>
          /* Panels mount one at a time (App.tsx), so the tab only controls
             a panel that exists right now — aria-controls never dangles. */
          settingsOpen || showingAI || extraView
            ? undefined
            : key === tab
              ? key === 'thread'
                ? 'chat-screen'
                : `panel-${key}`
              : undefined
        }
        onChange={k => {
          // Picking a tab is a way out of Settings — and out of the AI
          // screen, which has no tab of its own any more. Without this the
          // AI panel stayed mounted over whatever tab you picked.
          // Card 2Cium84y07TE: the pick goes through navigateTo, so a tab is
          // a real destination — it writes #search into the address and Back
          // retraces the tabs the same way it retraces a deep link.
          navigateTo({ section: k as Tab });
        }}
        id="pear-tabs"
        trailing={
          <>
          <button
            type="button"
            className="pear-btn pear-tab more-slot"
            id="btn-more"
            aria-label="Explore"
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen(true)}
          >
            <span className="tab-icon more-amp" aria-hidden="true">
              &amp;
            </span>
            {/* The word, not just the glyph: the slot reads "&" alone and
                nobody can tell what it opens (founder report 2026-10-08 —
                "I don't see the text More next to &"). Same label span the
                five tabs carry, so the bar has one display. */}
            <span className="pear-tab-label">Explore</span>
          </button>
          {/* Collapse toggle (card quWhYyrNiIbt, PRD L2) — desktop only (the
              base .rail-collapse is display:none and only the >=641px block
              turns it on). A chevron that flips with the state, hidden label
              when collapsed like every other tab. */}
          <button
            type="button"
            className="pear-btn pear-tab rail-collapse"
            data-test="rail-collapse"
            aria-label={railCollapsed ? 'Expand menu' : 'Collapse menu'}
            aria-expanded={!railCollapsed}
            onClick={toggleRail}
          >
            <span className="tab-icon" aria-hidden="true">{railCollapsed ? '»' : '«'}</span>
            <span className="pear-tab-label">{railCollapsed ? 'Expand' : 'Collapse'}</span>
          </button>
          </>
        }
      />
    )}
    </>
  );
}
